import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

export type DateDetailMode = 'none' | 'present' | 'absent' | 'both';

export interface MemberReportRow {
  name: string;
  phone: string;
  meetingType: string;
  total: number;
  present: number;
  absent: number;
  percentage: number;
  presentDates?: string[];
  absentDates?: string[];
}

export interface MeetingReportRow {
  date: string;
  meetingType: string;
  convener: string;
  total: number;
  present: number;
  absent: number;
  percentage: number;
}

export interface SummaryReportRow {
  meetingType: string;
  totalMeetings: number;
  totalMembers: number;
  totalPresent: number;
  totalAbsent: number;
  percentage: number;
}

export interface ReportHeaderOptions {
  reportTitle: string;
  subtitle?: string;
  filterSummary?: string[];
  dateRangeText?: string;
  dateDetailMode?: DateDetailMode;
}

const BRAND_BLUE = [37, 99, 235]; // Blue 600
const TEXT_DARK = [30, 41, 59];   // Slate 800
const TEXT_MUTED = [100, 116, 139]; // Slate 500

/**
 * Draw header section on PDF document
 */
function drawPDFHeader(doc: any, options: ReportHeaderOptions): number {
  const pageWidth = doc.internal.pageSize.width;
  
  // Top Banner
  doc.setFillColor(BRAND_BLUE[0], BRAND_BLUE[1], BRAND_BLUE[2]);
  doc.rect(0, 0, pageWidth, 28, 'F');
  
  // Header Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('STATE MEETING TRACKER', 14, 18);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('SSF KERALA', pageWidth - 14, 18, { align: 'right' });

  // Report Title
  doc.setTextColor(TEXT_DARK[0], TEXT_DARK[1], TEXT_DARK[2]);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(options.reportTitle.toUpperCase(), 14, 38);

  // Subtitle / Generated date
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(TEXT_MUTED[0], TEXT_MUTED[1], TEXT_MUTED[2]);
  
  const generatedText = `Generated: ${new Date().toLocaleString()}`;
  doc.text(generatedText, 14, 45);

  let currentY = 52;

  // Active Filters Box
  const filterParts = [...(options.filterSummary || [])];
  if (options.dateRangeText) {
    filterParts.unshift(`Date Filter: ${options.dateRangeText}`);
  }
  if (options.dateDetailMode && options.dateDetailMode !== 'none') {
    const modeLabel = 
      options.dateDetailMode === 'present' ? 'Dates of Attendance' :
      options.dateDetailMode === 'absent' ? 'Dates of Absence' : 'Present & Absent Dates';
    filterParts.push(`Date Details Shown: ${modeLabel}`);
  }

  if (filterParts.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(50, 50, 50);
    doc.text('Report Details & Active Filters:', 14, currentY);

    currentY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(TEXT_MUTED[0], TEXT_MUTED[1], TEXT_MUTED[2]);

    filterParts.forEach((filter) => {
      doc.text(`• ${filter}`, 18, currentY);
      currentY += 4.5;
    });

    currentY += 2;
  }

  return currentY;
}

/**
 * Add page numbering footer to all pages
 */
function addPDFFooter(doc: any) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(TEXT_MUTED[0], TEXT_MUTED[1], TEXT_MUTED[2]);
    doc.setFont('helvetica', 'normal');
    
    // Line separator
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(14, pageHeight - 14, pageWidth - 14, pageHeight - 14);

    doc.text('State Meeting Tracker • SSF Kerala', 14, pageHeight - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
  }
}

/**
 * Format date array into comma-separated readable string
 */
function formatDateList(dates?: string[]): string {
  if (!dates || dates.length === 0) return 'None';
  return dates.map(d => {
    try {
      const dateObj = new Date(d);
      if (isNaN(dateObj.getTime())) return d;
      return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return d;
    }
  }).join(', ');
}

export class PDFReportGenerator {
  /**
   * Export Member-Wise Attendance Report to PDF
   */
  static generateMemberWisePDF(
    data: MemberReportRow[],
    options: ReportHeaderOptions
  ) {
    if (!data || data.length === 0) return;

    const mode = options.dateDetailMode || 'none';
    const isLandscape = mode === 'both' || mode === 'present' || mode === 'absent';
    const doc: any = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait' });

    let startY = drawPDFHeader(doc, options);

    let headers: string[] = ['Member Name', 'Phone', 'Meeting Type', 'Total', 'Present', 'Absent', 'Attendance %'];
    
    if (mode === 'present') {
      headers.push('Dates of Attendance (Present)');
    } else if (mode === 'absent') {
      headers.push('Dates of Absence (Absent)');
    } else if (mode === 'both') {
      headers.push('Dates of Attendance (Present)', 'Dates of Absence (Absent)');
    }

    const tableRows = data.map(row => {
      const baseRow = [
        row.name,
        row.phone || 'N/A',
        row.meetingType,
        row.total.toString(),
        row.present.toString(),
        row.absent.toString(),
        `${row.percentage.toFixed(1)}%`
      ];

      if (mode === 'present') {
        baseRow.push(formatDateList(row.presentDates));
      } else if (mode === 'absent') {
        baseRow.push(formatDateList(row.absentDates));
      } else if (mode === 'both') {
        baseRow.push(formatDateList(row.presentDates));
        baseRow.push(formatDateList(row.absentDates));
      }

      return baseRow;
    });

    autoTable(doc, {
      head: [headers],
      body: tableRows,
      startY: startY,
      theme: 'grid',
      styles: {
        fontSize: isLandscape ? 8 : 8.5,
        cellPadding: 3.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: BRAND_BLUE,
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: isLandscape ? 38 : 'auto' },
        1: { cellWidth: isLandscape ? 28 : 'auto' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center' },
        6: { halign: 'center', fontStyle: 'bold' }
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      }
    });

    addPDFFooter(doc);
    const dateStamp = new Date().toISOString().slice(0, 10);
    doc.save(`Member-Attendance-Report-${dateStamp}.pdf`);
  }

  /**
   * Export Meeting-Wise Report to PDF
   */
  static generateMeetingWisePDF(
    data: MeetingReportRow[],
    options: ReportHeaderOptions
  ) {
    if (!data || data.length === 0) return;

    const doc: any = new jsPDF();
    let startY = drawPDFHeader(doc, options);

    const headers = ['Meeting Date', 'Meeting Type', 'Convener', 'Total Members', 'Present', 'Absent', 'Attendance %'];
    const tableRows = data.map(row => [
      row.date,
      row.meetingType,
      row.convener || 'N/A',
      row.total.toString(),
      row.present.toString(),
      row.absent.toString(),
      `${row.percentage.toFixed(1)}%`
    ]);

    autoTable(doc, {
      head: [headers],
      body: tableRows,
      startY: startY,
      theme: 'grid',
      styles: {
        fontSize: 8.5,
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: BRAND_BLUE,
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { fontStyle: 'bold' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center' },
        6: { halign: 'center', fontStyle: 'bold' }
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      }
    });

    addPDFFooter(doc);
    const dateStamp = new Date().toISOString().slice(0, 10);
    doc.save(`Meeting-Wise-Report-${dateStamp}.pdf`);
  }

  /**
   * Export Summary Report to PDF
   */
  static generateSummaryPDF(
    data: SummaryReportRow[],
    options: ReportHeaderOptions
  ) {
    if (!data || data.length === 0) return;

    const doc: any = new jsPDF();
    let startY = drawPDFHeader(doc, options);

    const headers = ['Meeting Type', 'Total Meetings', 'Total Members', 'Total Present', 'Total Absent', 'Avg Attendance %'];
    const tableRows = data.map(row => [
      row.meetingType,
      row.totalMeetings.toString(),
      row.totalMembers.toString(),
      row.totalPresent.toString(),
      row.totalAbsent.toString(),
      `${row.percentage.toFixed(1)}%`
    ]);

    autoTable(doc, {
      head: [headers],
      body: tableRows,
      startY: startY,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: BRAND_BLUE,
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center', fontStyle: 'bold' }
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      }
    });

    addPDFFooter(doc);
    const dateStamp = new Date().toISOString().slice(0, 10);
    doc.save(`Attendance-Summary-Report-${dateStamp}.pdf`);
  }

  /**
   * Export Single Meeting Instance Attendance PDF Report
   */
  static generateSingleMeetingPDF(meeting: {
    meetingTitle: string;
    meetingType?: string;
    date: string;
    time?: string;
    convenerName?: string;
    presentRecords: Array<{ name: string; phone?: string }>;
    absentRecords: Array<{ name: string; phone?: string }>;
  }) {
    const doc: any = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    // Header Banner
    doc.setFillColor(BRAND_BLUE[0], BRAND_BLUE[1], BRAND_BLUE[2]);
    doc.rect(0, 0, pageWidth, 28, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('STATE MEETING TRACKER', 14, 18);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('SSF KERALA', pageWidth - 14, 18, { align: 'right' });

    // Meeting Details Box
    doc.setTextColor(TEXT_DARK[0], TEXT_DARK[1], TEXT_DARK[2]);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(meeting.meetingTitle.toUpperCase(), 14, 38);

    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(TEXT_MUTED[0], TEXT_MUTED[1], TEXT_MUTED[2]);
    
    let infoY = 46;
    doc.text(`Meeting Date: ${meeting.date}${meeting.time ? `  |  Time: ${meeting.time}` : ''}`, 14, infoY);
    infoY += 5;

    if (meeting.meetingType) {
      doc.text(`Meeting Category: ${meeting.meetingType}`, 14, infoY);
      infoY += 5;
    }
    if (meeting.convenerName) {
      doc.text(`Convener: ${meeting.convenerName}`, 14, infoY);
      infoY += 5;
    }

    const total = meeting.presentRecords.length + meeting.absentRecords.length;
    const presentCount = meeting.presentRecords.length;
    const absentCount = meeting.absentRecords.length;
    const rate = total > 0 ? ((presentCount / total) * 100).toFixed(1) : '0';

    // Stat Summary Cards Row in PDF
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(14, infoY, pageWidth - 28, 16, 2, 2, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(TEXT_DARK[0], TEXT_DARK[1], TEXT_DARK[2]);

    const statText = `Total Members: ${total}   |   Present: ${presentCount} (${rate}%)   |   Absent: ${absentCount}`;
    doc.text(statText, 20, infoY + 10);

    let startY = infoY + 22;

    // Present Members Table
    if (meeting.presentRecords.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(22, 101, 52); // Dark Green
      doc.text(`✅ Present Members (${presentCount})`, 14, startY);
      startY += 4;

      const presentBody = meeting.presentRecords.map((r, i) => [
        (i + 1).toString(),
        r.name,
        r.phone || 'N/A',
        'Present'
      ]);

      autoTable(doc, {
        head: [['#', 'Member Name', 'Phone', 'Status']],
        body: presentBody,
        startY: startY,
        theme: 'grid',
        styles: { fontSize: 8.5, cellPadding: 3 },
        headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 12, halign: 'center' }, 3: { halign: 'center' } }
      });

      startY = (doc as any).lastAutoTable.finalY + 10;
    }

    // Absent Members Table
    if (meeting.absentRecords.length > 0) {
      // Check page break room
      if (startY > doc.internal.pageSize.height - 50) {
        doc.addPage();
        startY = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(153, 27, 27); // Dark Red
      doc.text(`❌ Absentee List (${absentCount})`, 14, startY);
      startY += 4;

      const absentBody = meeting.absentRecords.map((r, i) => [
        (i + 1).toString(),
        r.name,
        r.phone || 'N/A',
        'Absent'
      ]);

      autoTable(doc, {
        head: [['#', 'Member Name', 'Phone', 'Status']],
        body: absentBody,
        startY: startY,
        theme: 'grid',
        styles: { fontSize: 8.5, cellPadding: 3 },
        headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 12, halign: 'center' }, 3: { halign: 'center' } }
      });
    }

    addPDFFooter(doc);
    const dateStamp = meeting.date.replace(/[^0-9-]/g, '_');
    doc.save(`Meeting-Attendance-${dateStamp}.pdf`);
  }
}
